import React from 'react';

import PropTypes from 'prop-types';

import ResourceProperty from '../../../../common/resource/ResourceProperty';

import { Table } from '../../../../common/Table';

import { numberFormatter } from '../../../../../utils/number';

import Alert from '../../../../common/informative/Alert';

import { THERE_ARE_NOT_DATA_TO_SHOW } from '../../../../../config/messages';

import LiquidationGeneralIncomeDetailResource from './LiquidationGeneralIncomeDetailResource';

import {
  excessBaggageSummaryPropTypes,
  parcelIncomeSummaryPropTypes,
} from '../../salesSession/SalesSessionPropTypes';

import Badge from '../../../../common/Badge';

const LiquidationIncomeResource = ({
  ticketSummaryList,
  generalIncomeSummaryList,
  generalIncomeTypeSummaryList,
  generalIncomePaymentMethodSummaryList,
  cashPaymentMethodList,
  excessBaggageSummaryList,
  totalSalesPaymentMethodSummaryList,
  parcelIncomeSummaryList,
  debitNoteIncomeSummaryList,
}) => {
  const payVoucherData = ticketSummaryList.map(
    ({ documentSeries, totalAmount, voucherType, contingency }) => ({
      type: 'Pasaje',
      documentSeries,
      totalAmount,
      voucherType,
      contingency,
      isDebitNote: false,
    }),
  );

  excessBaggageSummaryList.forEach((excess) => {
    payVoucherData.push({
      type: 'Equipaje',
      documentSeries: excess.documentSeries,
      totalAmount: excess.activeExcessBaggageAmount,
      voucherType: excess.voucherType,
      contingency: excess.contingency,
      isDebitNote: false,
    });
  });

  parcelIncomeSummaryList.forEach((parcel) => {
    payVoucherData.push({
      type: 'Cargo',
      documentSeries: parcel.documentSeries,
      totalAmount: parcel.totalAmount,
      voucherType: parcel.voucherType,
      contingency: parcel.contingency,
      isDebitNote: false,
    });
  });

  // Debit Notes
  debitNoteIncomeSummaryList.forEach((debitNote) => {
    payVoucherData.push({
      type: 'Nota de Débito',
      documentSeries: debitNote.documentSeries,
      totalAmount: debitNote.totalAmount,
      voucherType: 'Nota de Débito',
      contingency: false,
      isDebitNote: true,
    });
  });

  const clearVoucherPrefix = (serie) => {
    if (serie && (serie.startsWith('B') || serie.startsWith('F'))) {
      return serie.substring(1);
    }

    return serie;
  };

  let payVoucherTable = (
    <Alert type="warning" message={THERE_ARE_NOT_DATA_TO_SHOW} />
  );

  if (
    ticketSummaryList.length > 0 ||
    parcelIncomeSummaryList.length > 0 ||
    excessBaggageSummaryList.length > 0 ||
    debitNoteIncomeSummaryList.length > 0
  ) {
    payVoucherTable = (
      <Table
        columns={[
          {
            Header: 'Tipo',
            accessor: 'voucherType',
            className: 'text-center',
            Cell: (row) => {
              if (row.original.isDebitNote) {
                return (
                  <Badge
                    className="bg-success text-white"
                    text="Nota de Débito"
                  />
                );
              }

              if (row.original.contingency) {
                const text = row.value.replace(/electronica/gi, '');

                return (
                  <>
                    {text} -{' '}
                    <Badge
                      className="bg-success text-white"
                      text={row.original.type}
                    />{' '}
                    <Badge
                      className="bg-secondary text-white"
                      text="Contingencia"
                    />
                  </>
                );
              }

              return (
                <>
                  {row.value} -{' '}
                  <Badge
                    className="bg-success text-white"
                    text={row.original.type}
                  />
                </>
              );
            },
          },
          {
            Header: 'Serie',
            accessor: 'documentSeries',
            className: 'text-center',
            Cell: (row) => {
              if (row.original.contingency) {
                return clearVoucherPrefix(row.value);
              }

              return row.value;
            },
          },
          {
            Header: 'Total de Ventas',
            accessor: 'totalAmount',
            Cell: ({ value }) => numberFormatter({ value }),
            Footer: (row) =>
              `Total: ${numberFormatter({
                value: row.data.reduce(
                  (accumulated, data) => accumulated + data.totalAmount,
                  0,
                ),
              })}`,
            className: 'text-right',
          },
        ]}
        data={payVoucherData}
        defaultPageSize={payVoucherData.length}
        showPagination={false}
      />
    );
  }

  let paymentMethodTable = (
    <Alert type="warning" message={THERE_ARE_NOT_DATA_TO_SHOW} />
  );

  const orderedTotalSalesPaymentMethodSummaryListByAlfabet =
    totalSalesPaymentMethodSummaryList.slice().sort((a, b) => {
      if (a.moneyFlowConcept < b.moneyFlowConcept) return -1;
      if (a.moneyFlowConcept > b.moneyFlowConcept) return 1;

      return 0;
    });

  if (orderedTotalSalesPaymentMethodSummaryListByAlfabet.length > 0) {
    paymentMethodTable = (
      <Table
        columns={[
          {
            Header: 'Método de Pago',
            accessor: 'moneyFlowConcept',
            className: 'text-center',
          },
          {
            Header: 'Total',
            accessor: 'totalAmount',
            Cell: ({ value }) => numberFormatter({ value }),
            Footer: (row) =>
              `Total: ${numberFormatter({
                value: row.data.reduce(
                  (accumulated, data) => accumulated + data.totalAmount,
                  0,
                ),
              })}`,
            className: 'text-right',
          },
        ]}
        data={orderedTotalSalesPaymentMethodSummaryListByAlfabet}
        defaultPageSize={
          orderedTotalSalesPaymentMethodSummaryListByAlfabet.length
        }
        showPagination={false}
      />
    );
  }

  let otherIncomes = 0;

  generalIncomePaymentMethodSummaryList.forEach((row) => {
    const foundCash = cashPaymentMethodList.find(
      (paymentMethod) => row.moneyFlowConcept === paymentMethod.name,
    );

    if (foundCash) {
      otherIncomes += row.totalAmount;
    }
  });

  let totalCash = 0;

  totalSalesPaymentMethodSummaryList.forEach((row) => {
    const foundCash = cashPaymentMethodList.find(
      (paymentMethod) => row.moneyFlowConcept === paymentMethod.name,
    );

    if (foundCash) {
      totalCash += row.totalAmount;
    }
  });

  const total = otherIncomes + totalCash;

  return (
    <>
      <hr />

      <h3>Ingresos</h3>

      <div className="mb-3">
        <h5>Tipos de Comprobante de Pago</h5>

        {payVoucherTable}

        {paymentMethodTable}
      </div>

      <LiquidationGeneralIncomeDetailResource
        generalIncomeSummaryList={generalIncomeSummaryList}
        generalIncomePaymentMethodSummaryList={
          generalIncomePaymentMethodSummaryList
        }
        generalIncomeTypeSummaryList={generalIncomeTypeSummaryList}
        cashPaymentMethodList={cashPaymentMethodList}
      />

      <ResourceProperty label="Efectivo total por Comprobante de Pago:">
        {numberFormatter({ value: totalCash })}
      </ResourceProperty>

      <h4 className="text-right">
        Ingresos Totales: {numberFormatter({ value: total })}
      </h4>
    </>
  );
};

LiquidationIncomeResource.propTypes = {
  ticketSummaryList: PropTypes.arrayOf(
    PropTypes.shape({
      currency: PropTypes.string,
      rate: PropTypes.number,
    }),
  ),

  generalIncomeSummaryList: PropTypes.arrayOf(
    PropTypes.shape({
      documentSeries: PropTypes.string,
      totalAmount: PropTypes.number,
    }),
  ),

  generalIncomeTypeSummaryList: PropTypes.arrayOf(
    PropTypes.shape({
      moneyFlowConcept: PropTypes.string,
      totalAmount: PropTypes.number,
    }),
  ),

  generalIncomePaymentMethodSummaryList: PropTypes.arrayOf(
    PropTypes.shape({
      moneyFlowConcept: PropTypes.string,
      totalAmount: PropTypes.number,
    }),
  ),

  cashPaymentMethodList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ).isRequired,

  totalSalesPaymentMethodSummaryList: PropTypes.arrayOf(
    PropTypes.shape({
      transactionCode: PropTypes.string,
      moneyFlowConcept: PropTypes.string,
      totalAmount: PropTypes.number,
    }),
  ),

  excessBaggageSummaryList: excessBaggageSummaryPropTypes,

  parcelIncomeSummaryList: parcelIncomeSummaryPropTypes,

  debitNoteIncomeSummaryList: PropTypes.arrayOf(
    PropTypes.shape({
      documentSeries: PropTypes.string,
      numberOfDebitNotes: PropTypes.number,
      totalAmount: PropTypes.number,
    }),
  ),
};

LiquidationIncomeResource.defaultProps = {
  ticketSummaryList: [],
  generalIncomeSummaryList: [],
  generalIncomeTypeSummaryList: [],
  generalIncomePaymentMethodSummaryList: [],
  totalSalesPaymentMethodSummaryList: [],
  excessBaggageSummaryList: [],
  parcelIncomeSummaryList: [],
  debitNoteIncomeSummaryList: [],
};

export default LiquidationIncomeResource;
